import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../../page.css";
import "../../home.css";
import "../../trekking/kilimanjaro.css";
import "./serengeti.css";
import "./ngorongoro.css";

const features = [
  {
    title: "A collapsed volcano",
    text: "Ngorongoro Crater is a vast caldera. Steep walls hold a self contained world of grassland, forest, lakes, and wildlife on the floor below.",
  },
  {
    title: "Dense game viewing",
    text: "Lions, elephants, buffalo, hippos, and rare black rhinos share a compact arena. Sightings often come quickly once you descend.",
  },
  {
    title: "Rim above the clouds",
    text: "Lodges sit on the highland rim with cool air and wide views into the crater. Mornings can start above mist before the drive down.",
  },
  {
    title: "Perfect northern link",
    text: "Ngorongoro sits between Arusha and the Serengeti. Most private circuits spend one or two nights here as a highlight stop.",
  },
];

const parkStats = [
  { value: "260", label: "km² crater floor" },
  { value: "600 m", label: "wall depth" },
  { value: "Big 5", label: "all present" },
  { value: "UNESCO", label: "World Heritage Site" },
];

const experiences = [
  {
    id: "crater-floor",
    name: "Crater Floor Game Drives",
    days: "Full day in the crater",
    style: "Classic wildlife day",
    lodging: "Rim lodge or tented camp",
    bestFor: "Big sightings in a single dramatic landscape",
    image:
      "https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open grassland and wildlife under wide African sky",
    summary:
      "You descend the crater wall after breakfast and spend the day on the floor among herds, wetlands, and predator country before climbing out before dark.",
    body: "The crater floor is the reason most guests come. Distances are short compared with the Serengeti, so a well paced day can cover grassland, Lerai Forest edges, and lake margins. Picnic lunch is usually taken at a designated site. Vehicles must leave the crater by late afternoon, so timing and a strong guide matter.",
    highlights: [
      "High chance of lion and elephant sightings",
      "Black rhino when conditions and luck align",
      "Hippo pools and flamingo edges on the lakes",
      "One of the densest wildlife arenas in Tanzania",
    ],
    itinerary: [
      { day: "Morning", text: "Descend the crater wall and begin game drives on the floor." },
      { day: "Midday", text: "Picnic lunch at an approved crater site." },
      { day: "Afternoon", text: "Continue toward forest edges, open plains, and water." },
      { day: "Exit", text: "Climb back to the rim before park closing time." },
    ],
    notes:
      "A single crater day is powerful. Two nights on the rim let you enter early and keep the pace unhurried.",
  },
  {
    id: "rim-lodges",
    name: "Crater Rim Lodges",
    days: "1 to 3 nights",
    style: "Highland lodge stay",
    lodging: "Rim lodge or luxury tented camp",
    bestFor: "Views, cool nights, and easy crater access",
    image:
      "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Highland landscape under open sky",
    summary:
      "Stay on the crater rim where evenings are cooler and mornings look down into the caldera. Lodges vary from classic to high end, all chosen for access and outlook.",
    body: "Rim lodging is part of the Ngorongoro experience. You wake above the crater, dress warmer than on the plains, and drive down when the gate opens. After the descent day, sunset on the rim and a quiet dinner feel like a different climate from Serengeti camps.",
    highlights: [
      "Dramatic crater viewpoints near many lodges",
      "Cooler highland climate after hot plains days",
      "Simple logistics for an early crater descent",
      "Strong overnight stop between Arusha and Serengeti",
    ],
    itinerary: [
      { day: "Arrival", text: "Drive up from Karatu or Arusha and settle on the rim." },
      { day: "Viewpoint", text: "Optional crater overlook before dinner." },
      { day: "Safari day", text: "Full crater floor drive with picnic lunch." },
      { day: "Next", text: "Continue to Serengeti, Tarangire, or return toward Arusha." },
    ],
    notes:
      "Ask for rim rooms with crater views when available. We match lodge style to your budget and the rest of the circuit.",
  },
  {
    id: "highlands",
    name: "Ngorongoro Highlands",
    days: "Half day to 1 night",
    style: "Culture and scenery",
    lodging: "Rim lodge or highland camp",
    bestFor: "Maasai culture and quieter highland roads",
    image:
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Open highland slopes and distant ridges",
    summary:
      "Outside the crater floor, the conservation area includes highland pastures, Maasai communities, and roads that connect Karatu to the Serengeti gate.",
    body: "The highlands give context to the crater. Cultural visits, when arranged respectfully with local hosts, add people and place to a wildlife heavy itinerary. The drive itself is scenic, with farming valleys giving way to cooler forest and open grazing land.",
    highlights: [
      "Optional Maasai village visits arranged with care",
      "Beautiful transit between crater and Serengeti",
      "Cooler air and different light from the plains",
      "Useful buffer day if you want a softer pace",
    ],
    itinerary: [
      { day: "Morning", text: "Leave lodge for a highland viewpoint or community visit." },
      { day: "Midday", text: "Return for lunch or continue toward your next park." },
      { day: "Optional", text: "Short walk near lodge grounds where permitted." },
      { day: "Evening", text: "Rim sunset and dinner at camp or lodge." },
    ],
    notes:
      "Cultural stops are optional. Tell us if you prefer a pure wildlife focus or a mixed day.",
  },
  {
    id: "empakaai",
    name: "Empakaai and Olmoti",
    days: "Full day add on",
    style: "Walking crater day",
    lodging: "Rim lodge base",
    bestFor: "Active travelers who want a quieter crater hike",
    image:
      "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Mountain rim and open highland country",
    summary:
      "Smaller calderas near Ngorongoro offer guided walks to rim views and, at Empakaai, a steep path toward a flamingo lake when conditions allow.",
    body: "Empakaai and Olmoti suit guests who want movement beyond the vehicle. These days need fitness, park rules, and a ranger guide. They are not for every itinerary, but they add a strong highland chapter when you have an extra day and the group wants to walk.",
    highlights: [
      "Fewer vehicles than the main crater floor",
      "Rim views and highland forest approaches",
      "Empakaai lake walk when open and suitable",
      "Memorable for photographers and walkers",
    ],
    itinerary: [
      { day: "Early", text: "Transfer with ranger guide to the trailhead." },
      { day: "Walk", text: "Descend or rim walk depending on the chosen crater." },
      { day: "Midday", text: "Packed lunch and rest on the trail plan." },
      { day: "Return", text: "Climb out and drive back to your rim lodge." },
    ],
    notes:
      "Walking days depend on fitness, weather, and park permissions. We confirm details before locking the plan.",
  },
];

const seasons = [
  {
    title: "Dry months",
    text: "June to October. Clearer crater views, easier roads, and strong game concentration around water on the floor.",
  },
  {
    title: "Green months",
    text: "November to May. Lush walls and floor, beautiful light, some afternoon rain, and fewer vehicles in shoulder weeks.",
  },
  {
    title: "Best wildlife",
    text: "Year round on the crater floor. Dry season often feels more reliable for rhino and open grassland viewing.",
  },
  {
    title: "Pairing tip",
    text: "Combine with Serengeti for migration drama, or Tarangire for elephants, then finish on Zanzibar if you want the coast.",
  },
];

const practical = [
  {
    title: "How long to stay",
    text: "One night works for a single crater day. Two nights feel better if you want an early descent and a calmer evening on the rim.",
  },
  {
    title: "Crater rules",
    text: "Most visits are day drives only. Vehicles must exit by a set afternoon time, and off road driving is restricted.",
  },
  {
    title: "What we arrange",
    text: "Conservation fees, rim lodging, private 4x4 and guide, picnic lunch in the crater, and the link to your next park.",
  },
];

function Ngorongoro() {
  const { hash } = useLocation();
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    document.title = "Ngorongoro Crater · Mirrorkhan Tours";
  }, []);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [hash]);

  useEffect(() => {
    const node = statsRef.current;
    if (!node) return undefined;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setStatsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setStatsVisible(true);
        observer.disconnect();
      },
      { threshold: 0.25 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="kili-page ngorongoro-page">
      <section className="kili-hero serengeti-hero ngorongoro-hero">
        <div className="kili-hero__media" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1800&q=80"
            alt=""
          />
        </div>
        <div className="kili-hero__shade" aria-hidden="true" />
        <div className="container kili-hero__content">
          <div className="row">
            <div className="col-lg-8 col-xl-7">
              <p className="eyebrow eyebrow--light mb-3">
                <Link
                  to="/safaris/northern-circuits"
                  className="text-decoration-none"
                >
                  Northern Circuit
                </Link>
              </p>
              <h1 className="mb-3">Ngorongoro Crater</h1>
              <p className="kili-hero__lede mb-4">
                A volcanic caldera packed with wildlife. Descend from cool rim
                lodges to one of Tanzania’s densest safari floors, then climb
                out for highland evenings above the crater.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a className="btn btn-mk-gold" href="#experiences">
                  View experiences
                </a>
                <Link className="btn btn-mk-ghost" to="/contact">
                  Ask about Ngorongoro
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={statsRef}
        className={
          statsVisible ? "serengeti-stats is-visible" : "serengeti-stats"
        }
        aria-label="Ngorongoro park facts"
      >
        <div className="container">
          <ul className="serengeti-stats__list list-unstyled mb-0">
            {parkStats.map((stat, index) => (
              <li
                className="serengeti-stats__item"
                key={stat.label}
                style={{ "--stat-delay": `${index * 120}ms` }}
              >
                <span className="serengeti-stats__value">{stat.value}</span>
                <span className="serengeti-stats__label">{stat.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">The crater</p>
              <h2 className="mb-3">Why Ngorongoro feels unlike any other park</h2>
              <p className="page-lead mb-0">
                It is a natural amphitheatre. Wildlife, water, and grassland sit
                inside steep volcanic walls, so a single day can feel complete.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {features.map((item) => (
              <div className="col-md-6" key={item.title}>
                <article className="kili-feature h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="experiences" className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Experiences</p>
              <h2 className="mb-3">How to shape your Ngorongoro stay</h2>
              <p className="page-lead mb-0">
                Most guests come for the crater floor. Extra time on the rim,
                in the highlands, or on a walking crater day can deepen the
                trip.
              </p>
            </div>
          </div>

          <div className="kili-routes">
            {experiences.map((item, index) => (
              <article
                className={
                  index % 2 === 1
                    ? "kili-route kili-route--flip"
                    : "kili-route"
                }
                key={item.id}
                id={item.id}
              >
                <div className="kili-route__top">
                  <div className="kili-route__media">
                    <img src={item.image} alt={item.imageAlt} />
                  </div>
                  <div className="kili-route__intro">
                    <p className="kili-route__meta mb-2">
                      {item.days}
                      <span aria-hidden="true"> · </span>
                      {item.style}
                    </p>
                    <h3 className="h3 mb-3">{item.name}</h3>
                    <p className="kili-route__summary">{item.summary}</p>
                    <p className="kili-route__body-copy mb-0">{item.body}</p>
                  </div>
                </div>

                <div className="kili-route__panel">
                  <dl className="kili-route__facts">
                    <div>
                      <dt>Best for</dt>
                      <dd>{item.bestFor}</dd>
                    </div>
                    <div>
                      <dt>Time</dt>
                      <dd>{item.days}</dd>
                    </div>
                    <div>
                      <dt>Lodging</dt>
                      <dd>{item.lodging}</dd>
                    </div>
                    <div>
                      <dt>Style</dt>
                      <dd>{item.style}</dd>
                    </div>
                  </dl>

                  <div className="row g-4">
                    <div className="col-lg-5">
                      <h4 className="kili-route__subhead">On the day</h4>
                      <ul className="trek-points mb-0">
                        {item.highlights.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="col-lg-7">
                      <h4 className="kili-route__subhead">Sample outline</h4>
                      <ol className="kili-itinerary list-unstyled mb-0">
                        {item.itinerary.map((step) => (
                          <li key={step.day}>
                            <strong>{step.day}</strong>
                            <span>{step.text}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>

                  <p className="kili-route__note mb-3">{item.notes}</p>
                  <Link className="text-link" to="/contact">
                    Ask about {item.name}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Seasons</p>
              <h2 className="mb-3">When to visit the crater</h2>
              <p className="page-lead mb-0">
                Wildlife stays strong through the year. Season mainly changes
                the light, the greenery, and how busy the descent road feels.
              </p>
            </div>
          </div>

          <div className="row g-4">
            {seasons.map((item) => (
              <div className="col-md-6 col-lg-3" key={item.title}>
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-block home-section--soft">
        <div className="container">
          <div className="row mb-4 mb-lg-5">
            <div className="col-lg-8">
              <p className="eyebrow">Before you go</p>
              <h2 className="mb-0">Practical notes from the desk</h2>
            </div>
          </div>

          <div className="row g-4">
            {practical.map((item) => (
              <div className="col-md-4" key={item.title}>
                <article className="approach-item h-100">
                  <h3 className="h4 mb-2">{item.title}</h3>
                  <p className="mb-0">{item.text}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-close">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <p className="eyebrow eyebrow--light">Plan your safari</p>
              <h2 className="mb-3">Tell us if Ngorongoro is a one night stop or a longer highland stay.</h2>
              <p className="mb-0 home-close__copy">
                We reply with rim lodge options, crater day timing, and how it
                links to Serengeti or a Zanzibar finish.
              </p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link className="btn btn-mk-gold" to="/contact">
                Contact the desk
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Ngorongoro;
